import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { Icon } from "@axum/ax-design";

export const SCROLL_TRACK = "o360-scroll-track";

const SUBPIXEL = 1;

interface More {
  start: boolean;
  end: boolean;
}

const NO_MORE: More = { start: false, end: false };

export const ScrollHint = styled.div`
  --o360-scroll-fade-start: 0px;
  --o360-scroll-fade-end: 0px;

  position: relative;
  display: flex;
  min-width: 0;
  max-width: 100%;

  &.has-more-start {
    --o360-scroll-fade-start: 56px;
  }

  &.has-more-end {
    --o360-scroll-fade-end: 56px;
  }

  & > .${SCROLL_TRACK} {
    min-width: 0;
    overflow-x: auto;
    scrollbar-width: none;
  }

  & > .${SCROLL_TRACK}::-webkit-scrollbar {
    display: none;
  }

  &.has-more-start > .${SCROLL_TRACK},
  &.has-more-end > .${SCROLL_TRACK} {
    -webkit-mask-image: linear-gradient(to right, transparent, #000 var(--o360-scroll-fade-start), #000 calc(100% - var(--o360-scroll-fade-end)), transparent);
    mask-image: linear-gradient(to right, transparent, #000 var(--o360-scroll-fade-start), #000 calc(100% - var(--o360-scroll-fade-end)), transparent);
  }

  & > .o360-scroll-more {
    position: absolute;
    top: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    padding: 0;
    border: 0;
    background: none;
    color: var(--ax-text-secondary);
    cursor: pointer;
  }

  & > .o360-scroll-more.is-start {
    left: 0;
  }

  & > .o360-scroll-more.is-end {
    right: 0;
  }
`;

export interface ScrollTracks {
  more: More;
  className: string;
  scroll: (direction: number) => void;
}

export const useSyncedScroll = (host: React.RefObject<HTMLElement>): ScrollTracks => {
  const [more, setMore] = useState<More>(NO_MORE);

  const tracks = useCallback(
    () => (host.current ? Array.from(host.current.querySelectorAll<HTMLElement>(`.${SCROLL_TRACK}`)) : []),
    [host],
  );

  const measure = useCallback(() => {
    const [first] = tracks();
    const next = first
      ? { start: first.scrollLeft > SUBPIXEL, end: first.scrollLeft + first.clientWidth < first.scrollWidth - SUBPIXEL }
      : NO_MORE;
    setMore(current => (current.start === next.start && current.end === next.end ? current : next));
  }, [tracks]);

  useEffect(measure);

  useEffect(() => {
    const root = host.current;
    if (!root) return undefined;
    // Cada pista que se acomoda desde acá avisa su propio scroll: tomarlo como origen frenaría el desplazamiento suave de la que se tocó.
    const driven = new WeakMap<Element, number>();
    const follow = (event: Event) => {
      const source = event.target as HTMLElement;
      if (!source.classList || !source.classList.contains(SCROLL_TRACK)) return;
      if (driven.get(source) === source.scrollLeft) {
        driven.delete(source);
        return;
      }
      tracks().forEach(track => {
        if (track === source || track.scrollLeft === source.scrollLeft) return;
        track.scrollLeft = source.scrollLeft;
        driven.set(track, track.scrollLeft);
      });
      measure();
    };
    root.addEventListener("scroll", follow, true);
    window.addEventListener("resize", measure);
    return () => {
      root.removeEventListener("scroll", follow, true);
      window.removeEventListener("resize", measure);
    };
  }, [host, tracks, measure]);

  const scroll = (direction: number) => {
    const [first] = tracks();
    if (first) first.scrollBy({ left: direction * first.clientWidth * 0.7, behavior: "smooth" });
  };

  return { more, className: `${more.start ? "has-more-start" : ""}${more.end ? " has-more-end" : ""}`, scroll };
};

interface ButtonsProps {
  hint: ScrollTracks;
  previous: string;
  next: string;
}

export const ScrollMoreButtons = ({ hint, previous, next }: ButtonsProps) => (
  <>
    {hint.more.start && (
      <button type="button" className="o360-scroll-more is-start" tabIndex={-1} aria-label={previous} onClick={() => hint.scroll(-1)}>
        <Icon name="chevron-left" />
      </button>
    )}
    {hint.more.end && (
      <button type="button" className="o360-scroll-more is-end" tabIndex={-1} aria-label={next} onClick={() => hint.scroll(1)}>
        <Icon name="chevron-right" />
      </button>
    )}
  </>
);
